import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {POLICY_TEMPLATE_FRAGMENT} from 'auth/PolicyConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProjectSettingsModal, ProjectSettingsModalProps} from 'file-browser/ProjectSettingsModal';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import runModal from 'ui-library/runModal';

import {
  UserProjectsListingQuery,
  UserProjectsListingQueryVariables,
} from './__generated/UserProjectsListingQuery';
import PoliceablePolicyTable, {
  Policeable,
  POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT,
} from './PoliceablePolicyTable';
import useLimitOffset from './usePoliceablePolicyTableLimitOffset';

export interface ProjectPolicyTableProps {
  userId: string;
}

export const USER_PROJECTS_LISTING_QUERY = gql`
  query UserProjectsListingQuery($userId: StrictString!, $limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: IDENTITY) {
    user(id: $userId) {
      ... on UserNode {
        id
        userProjectsListing(limitOffsetInput: $limitOffsetInput) {
          ... on PartyPoliceablesListingNode {
            ...PolicyTablePartyPoliceableListing
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    policyTemplate(policyType: DATA) {
      ...PolicyTemplate
    }
  }
  ${POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT}
  ${POLICY_TEMPLATE_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const principalType = 'user';

export default function ProjectPolicyTable(props: ProjectPolicyTableProps): JSX.Element | null {
  const [{limit, offset}, {setLimit, setOffset}] = useLimitOffset();

  const result = useBenchlingQuery<UserProjectsListingQuery, UserProjectsListingQueryVariables>(
    USER_PROJECTS_LISTING_QUERY,
    {
      variables: {
        userId: props.userId,
        limitOffsetInput: {
          limit,
          offset,
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load projects; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <PoliceablePolicyTable
        isLoading={true}
        resourceType="project"
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: 0}}
        principalType={principalType}
      />
    );
  } else {
    assert(
      !isInaccessible(result.data.user),
      `Expected ${principalType} currently being viewed to be readable`
    );
    return (
      <PoliceablePolicyTable
        isLoading={false}
        resourceType="project"
        icon={ModelTypeConstants.PROJECT}
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: result.data.user.userProjectsListing.total}}
        policyTemplate={result.data.policyTemplate}
        partyPoliceablesListing={result.data.user.userProjectsListing}
        openPoliceablePage={(policeable: Policeable) => {
          assert(policeable.__typename === 'ProjectNode', 'Expected ProjectNode');
          navigateToURL(policeable.url);
        }}
        openPoliceableSettingsPage={(policeable: Policeable) => {
          assert(policeable.__typename === 'ProjectNode', 'Expected ProjectNode');
          runModal<ProjectSettingsModalProps>(ProjectSettingsModal, {
            permissions: policeable.permissions,
            sourceId: policeable.id,
            onUpdate: () => {
              result.refetch();
              setOffset(0);
            },
          });
        }}
        principalType={principalType}
      />
    );
  }
}
